import { useState } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { Firestore } from 'firebase/firestore';
import { Unit } from '../../types';
import { Trash2 } from 'lucide-react';

interface BrandingTabProps {
  unit: Unit;
  setUnit: (unit: Unit) => void;
  canEdit: boolean;
  db: Firestore;
  storage: any;
  confirmAction: (options: any) => Promise<any>;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  refreshUnits: () => Promise<void>;
  Card: any;
  CardContent: any;
  CardDescription: any;
  CardHeader: any;
  CardTitle: any;
  Button: any;
  Input: any;
  Label: any;
}

export function BrandingTab({
  unit,
  setUnit,
  canEdit,
  db,
  storage,
  confirmAction,
  showSuccess,
  showError,
  refreshUnits,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
}: BrandingTabProps) {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(unit.settings?.logoUrl || null);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showError('Image size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Delete old logo if exists
      if (unit.settings?.logoUrl) {
        try {
          const oldLogoRef = ref(storage, `units/${unit.id}/logo/logo`);
          await deleteObject(oldLogoRef);
        } catch (error) {
          // Ignore if file doesn't exist
        }
      }

      // Upload new logo
      const logoRef = ref(storage, `units/${unit.id}/logo/logo`);
      await uploadBytes(logoRef, file);
      const logoUrl = await getDownloadURL(logoRef);

      // Update Firestore
      await updateDoc(doc(db, 'units', unit.id), {
        'settings.logoUrl': logoUrl,
      });

      setUnit({
        ...unit,
        settings: { ...unit.settings, logoUrl },
      });
      setLogoPreview(logoUrl);

      showSuccess('Logo uploaded successfully!');
      await refreshUnits();
    } catch (error) {
      console.error('Error uploading logo:', error);
      showError('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    const result = await confirmAction({
      title: 'Remove Logo?',
      text: 'This will permanently delete your unit logo.',
    });

    if (result.isConfirmed) {
      setSaving(true);
      try {
        if (unit.settings?.logoUrl) {
          const logoRef = ref(storage, `units/${unit.id}/logo/logo`);
          await deleteObject(logoRef);
        }

        await updateDoc(doc(db, 'units', unit.id), {
          'settings.logoUrl': null,
        });

        setUnit({
          ...unit,
          settings: { ...unit.settings, logoUrl: undefined },
        });
        setLogoPreview(null);

        showSuccess('Logo removed successfully!');
        await refreshUnits();
      } catch (error) {
        console.error('Error removing logo:', error);
        showError('Failed to remove logo');
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Unit Logo</CardTitle>
          <CardDescription>Upload and manage your unit logo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {logoPreview && (
            <div>
              <Label>Current Logo</Label>
              <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                <img
                  src={logoPreview}
                  alt="Unit Logo"
                  className="h-32 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {canEdit && (
            <div className="space-y-2">
              <Label htmlFor="logoUpload">Upload New Logo</Label>
              <div className="flex gap-2">
                <Input
                  id="logoUpload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                  className="flex-1"
                />
                {logoPreview && (
                  <Button
                    variant="outline"
                    onClick={handleRemoveLogo}
                    disabled={uploadingLogo || saving}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Recommended: PNG or JPG, max 2MB, square aspect ratio
              </p>
              {uploadingLogo && (
                <div className="text-sm text-muted-foreground">Uploading...</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Brand Colors</CardTitle>
          <CardDescription>Customize your unit's appearance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="brandColor">Primary Brand Color</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                id="brandColor"
                type="color"
                value={unit.settings?.brandColor || '#1E3A5F'}
                onChange={async (e: any) => {
                  if (!canEdit) return;
                  try {
                    await updateDoc(doc(db, 'units', unit.id), {
                      'settings.brandColor': e.target.value,
                    });
                    setUnit({
                      ...unit,
                      settings: { ...unit.settings, brandColor: e.target.value },
                    });
                    showSuccess('Brand color updated!');
                  } catch (error) {
                    showError('Failed to update color');
                  }
                }}
                className="w-20 h-10"
                disabled={!canEdit}
              />
              <Input
                type="text"
                value={unit.settings?.brandColor || '#1E3A5F'}
                readOnly
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Used for headers, buttons, and branding elements
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

